'use client';

import React from 'react';
import Card from '@/components/ui/Card';
import { IconLoader, IconNewspaper, IconXCircle } from '@/components/ui/MonoIcons';

interface NewsSkeletonProps {
  count?: number;
  variant?: 'default' | 'compact' | 'featured';
}

export function NewsSkeleton({ count = 6, variant = 'default' }: NewsSkeletonProps) {
  const skeletons = Array.from({ length: count }, (_, i) => i);

  if (variant === 'compact') {
    return (
      <>
        {skeletons.map((i) => (
          <Card key={i} variant="glass" className="p-4">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-20 h-20 bg-site-raised/50 rounded-lg animate-pulse" />
              <div className="flex-1 space-y-2">
                <div className="h-5 bg-site-raised/50 rounded animate-pulse" />
                <div className="h-4 bg-site-raised/50 rounded animate-pulse w-3/4" />
                <div className="h-3 bg-site-raised/50 rounded animate-pulse w-1/2" />
              </div>
            </div>
          </Card>
        ))}
      </>
    );
  }

  if (variant === 'featured') {
    return (
      <>
        {skeletons.map((i) => (
          <Card key={i} variant="glass" className="overflow-hidden">
            <div className="h-64 bg-site-raised/50 animate-pulse" />
            <div className="p-4 space-y-3">
              <div className="h-6 bg-site-raised/50 rounded animate-pulse" />
              <div className="h-4 bg-site-raised/50 rounded animate-pulse w-2/3" />
              <div className="h-3 bg-site-raised/50 rounded animate-pulse w-1/3" />
            </div>
          </Card>
        ))}
      </>
    );
  }

  // Default variant
  return (
    <>
      {skeletons.map((i) => (
        <Card key={i} variant="glass" className="overflow-hidden">
          <div className="h-48 bg-site-raised/50 animate-pulse" />
          <div className="p-4 space-y-3">
            <div className="h-6 bg-site-raised/50 rounded animate-pulse" />
            <div className="space-y-2">
              <div className="h-4 bg-site-raised/50 rounded animate-pulse" />
              <div className="h-4 bg-site-raised/50 rounded animate-pulse w-5/6" />
            </div>
            <div className="flex gap-2">
              <div className="h-6 bg-site-raised/50 rounded-full animate-pulse w-16" />
              <div className="h-6 bg-site-raised/50 rounded-full animate-pulse w-20" />
            </div>
            <div className="flex items-center gap-4">
              <div className="h-4 bg-site-raised/50 rounded animate-pulse w-24" />
              <div className="h-4 bg-site-raised/50 rounded animate-pulse w-16" />
            </div>
          </div>
        </Card>
      ))}
    </>
  );
}

interface NewsErrorProps {
  error: string;
  onRetry?: () => void;
  className?: string;
}

export function NewsError({ error, onRetry, className = '' }: NewsErrorProps) {
  return (
    <Card variant="glass" className={`p-8 text-center ${className}`}>
      <IconXCircle className="w-16 h-16 mx-auto mb-4 text-site-secondary" />
      <h2 className="text-2xl font-bold text-site-text mb-4">Ошибка загрузки</h2>
      <p className="text-site-muted mb-6">{error}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="px-6 py-2 bg-site-accent hover:bg-site-accent text-site-on-accent rounded-lg transition-colors"
        >
          Попробовать снова
        </button>
      )}
    </Card>
  );
}

interface NewsEmptyProps {
  message?: string;
  className?: string;
}

export function NewsEmpty({ 
  message = 'Новости не найдены', 
  className = '' 
}: NewsEmptyProps) {
  return (
    <Card variant="glass" className={`p-8 text-center ${className}`}>
      <IconNewspaper className="w-16 h-16 mx-auto mb-4 text-site-secondary" />
      <h2 className="text-2xl font-bold text-site-text mb-4">{message}</h2>
      <p className="text-site-muted">
        Попробуйте изменить фильтры или поисковый запрос
      </p>
    </Card>
  );
}

interface NewsLoadingProps {
  message?: string;
  className?: string;
}

export function NewsLoading({ 
  message = 'Загружаем новости...', 
  className = '' 
}: NewsLoadingProps) {
  return (
    <div className={`flex items-center justify-center p-8 ${className}`}>
      <div className="text-center">
        <IconLoader className="w-12 h-12 animate-spin mx-auto mb-4 text-site-secondary" />
        <p className="text-xl text-site-secondary">{message}</p>
      </div>
    </div>
  );
}

